"use client";

import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { trackPhoneClick, trackWhatsAppClick } from "@/utils/gtm";
import { openWhatsApp, whatsappMessages } from "@/utils/whatsapp";
import LeadFormFields from "@/components/LeadForm/LeadFormFields";

const CONTACT_WIDGET_TARGET_ID = "contact-us-form-widget";

export function ContactMainBox() {
    const pathname = usePathname();
    const currentPage = pathname || "/";
    const [widgetFailed, setWidgetFailed] = useState(false);

    useEffect(() => {
        if (typeof document === "undefined") return;

        const target = document.getElementById(CONTACT_WIDGET_TARGET_ID);
        if (!target) return;

        setWidgetFailed(false);
        target.innerHTML = "";

        const script = document.createElement("script");
        script.src = "https://forms.innovlist.com/widget.js?type=starter";
        script.setAttribute("data-client", "seasoft");
        script.setAttribute("data-target", CONTACT_WIDGET_TARGET_ID);
        script.setAttribute("data-primary-color", "#155dfc");
        script.setAttribute("data-primary-hover", "#1447e6");
        script.async = true;

        script.onerror = () => {
            setWidgetFailed(true);
        };

        document.body.appendChild(script);

        return () => {
            script.remove();
        };
    }, []);

    return (
        <section className="py-8 md:py-12 bg-white">
            <div className="container mx-auto px-4">
                <div className="max-w-[1100px] mx-auto bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col md:flex-row">
                    {/* Left: Get In Touch */}
                    <div className="w-full md:w-[40%] p-0 md:p-10 lg:p-14 border-b md:border-b-0 md:border-r border-gray-100">
                        <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6 md:mb-10 text-left px-4 md:px-0 pt-6 md:pt-0">Get In Touch</h2>

                        <div className="px-4 md:px-0 space-y-4 md:space-y-8">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                                    <MapPin size={18} className="text-black" />
                                </div>
                                <div className="text-left">
                                    <h4 className="text-[#1A1A1A] font-bold text-base mb-1">Address</h4>
                                    <p className="text-[#666666] text-sm">15 Huntington Circle, Naperville, IL 60540, USA</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                                    <Phone size={18} className="text-black" />
                                </div>
                                <div className="text-left">
                                    <h4 className="text-[#1A1A1A] font-bold text-base mb-1">Phone</h4>
                                    <a
                                        href="tel:+17087669765"
                                        onClick={() => trackPhoneClick("+17087669765", currentPage)}
                                        className="text-[#666666] text-sm"
                                    >
                                        +1 (708) 766-9765
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div
                                    className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 sm:p-4 bg-[#f0fdf4] border border-[#25D366] rounded-xl cursor-pointer hover:bg-[#dcfce7] transition-colors w-full"
                                    onClick={() => {
                                        openWhatsApp(whatsappMessages.counseling);
                                        trackWhatsAppClick("contact_card", "counseling");
                                    }}
                                >
                                    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" className="text-[#25D366] flex-shrink-0">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-800 text-sm sm:text-base">WhatsApp</p>
                                        <p className="text-[#25D366] font-medium text-sm sm:text-base">+1 (209) 210-3187</p>
                                        <p className="text-xs sm:text-sm text-gray-500">Tap to chat - reply within minutes</p>
                                    </div>
                                    <span className="hidden sm:inline text-[#25D366] text-sm font-medium whitespace-nowrap flex-shrink-0">Chat Now →</span>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                                    <Mail size={18} className="text-black" />
                                </div>
                                <div className="text-left">
                                    <h4 className="text-[#1A1A1A] font-bold text-base mb-1">Email</h4>
                                    <p className="text-[#666666] text-sm hover:text-[#2563EB] cursor-pointer">training@seasoftclasses.com</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                                    <Clock size={18} className="text-black" />
                                </div>
                                <div className="text-left">
                                    <h4 className="text-[#1A1A1A] font-bold text-base mb-1">Office Hours</h4>
                                    <p className="text-[#666666] text-sm">Monday-Saturday, 9:00 AM - 7:00 PM CST</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 md:mt-14 pt-6 md:pt-8 border-t border-gray-50 px-4 md:px-0 hidden">
                            <h4 className="text-[#1A1A1A] font-bold text-base mb-4 md:mb-6 text-left">Social Media</h4>
                            <div className="flex items-center gap-3">
                                {/* Facebook */}
                                <a
                                    href="https://www.facebook.com/SeasoftClasses"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Visit Seasoft Classes Facebook page"
                                    title="Facebook"
                                    className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-[#2563EB] hover:text-white transition-all"
                                >
                                    <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    >
                                    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.791-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.887v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                                    </svg>
                                    <span className="sr-only">Facebook</span>
                                </a>

                                {/* X / Twitter */}
                                <a
                                    href="https://x.com/Seasoft_Classes"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Visit Seasoft Classes X profile"
                                    title="X (Twitter)"
                                    className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-[#2563EB] hover:text-white transition-all"
                                >
                                    <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    >
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                    <span className="sr-only">X (Twitter)</span>
                                </a>

                                {/* YouTube */}
                                <a
                                    href="https://www.youtube.com/user/ITTRAININGINUSA"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Visit Seasoft Classes YouTube channel"
                                    title="YouTube"
                                    className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-[#2563EB] hover:text-white transition-all"
                                >
                                    <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    >
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                    </svg>
                                    <span className="sr-only">YouTube</span>
                                </a>

                                {/* Pinterest */}
                                <a
                                    href="https://www.pinterest.com/seasoftclasses/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Visit Seasoft Classes Pinterest profile"
                                    title="Pinterest"
                                    className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-[#2563EB] hover:text-white transition-all"
                                >
                                    <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    >
                                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                                    </svg>
                                    <span className="sr-only">Pinterest</span>
                                </a>

                                {/* LinkedIn */}
                                <a
                                    href="https://www.linkedin.com/company/seasoft-classes"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Visit Seasoft Classes LinkedIn page"
                                    title="LinkedIn"
                                    className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-[#2563EB] hover:text-white transition-all"
                                >
                                    <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    >
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                    </svg>
                                    <span className="sr-only">LinkedIn</span>
                                </a>

                                </div>
                        </div>
                    </div>

                    {/* Right: Contact Form (inline, no popup) */}
                    <div className="flex-1 p-0 md:p-10 lg:p-14">
                        <LeadFormFields source="contact_page" heading="Start Your Journey" radius="rounded-xl" className="!p-4 md:!p-0" />
                    </div>
                </div>
            </div>
        </section>
    );
}
